import { useAuthContext } from '../authContext';

type Options = {
  method?: 'GET' | 'POST';
  body?: BodyInit | null;
  headers?: HeadersInit;
  sameOrigin?: boolean;
};

const BASEURL = process.env.NEXT_PUBLIC_API_URL; // For local development use http://localhost:3001
const CLIENTID = process.env.NEXT_PUBLIC_CLIENT_ID || '';

const useAuthenticatedFetch = () => {
  const { isLoggedIn, currentUser, getIdToken } = useAuthContext();

  return async (endPoint: string, options?: Options) => {
    if (!isLoggedIn || !currentUser) {
      console.info(
        'Calling useAuthenticatedFetch while not logged in, returning unauthenticated fetch',
      );
    }

    const headers = new Headers(options?.headers || {});
    if (!(options?.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('client-id', CLIENTID);

    if (isLoggedIn && currentUser) {
      const accessToken = await getIdToken();
      headers.set('authorization', `Bearer ${accessToken}`);
    }

    const { sameOrigin, ...requestOptions } = options ?? {};

    const fetchOptions: RequestInit = {
      ...requestOptions,
      ...(sameOrigin && { credentials: 'same-origin' }),
      headers,
    };

    // If BASEURL is not provided (e.g. in some local setups), fall back to
    // using the provided endpoint as a relative path so fetch targets the
    // current origin. Log to help debugging.
    if (!BASEURL && !sameOrigin) {
      console.warn(
        'NEXT_PUBLIC_API_URL not set, using relative endpoint for fetch:',
        endPoint,
      );
    }

    const fetchUrl =
      BASEURL && !sameOrigin
        ? endPoint.startsWith('/')
          ? `${BASEURL}${endPoint}`
          : `${BASEURL}/${endPoint}`
        : endPoint; // relative

    console.info('fetchUrl: ', fetchUrl);

    return fetch(fetchUrl, fetchOptions);
  };
};

export default useAuthenticatedFetch;
